import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import { v4 as uuid } from 'uuid';
import SubOutcomeRow from './SubOutcomeRow';
import { cloneDeep } from 'lodash';
import { ActivityTypeOutcome } from '../OutcomList/types/types';
import { SubOutcome } from './types/types';
import useOptions from '../../../../hooks/useOptions';
import useAwaitableComponent from '../../../../hooks/useAwaitableComponent';
import { client } from '../../../../utils/axios-client';
import ApplicationStore from '../../../../stores/ApplicationStore';
import { ApiResponseType } from '../../../../types';
import FallbackSpinner from '../../../../materio/@core/components/spinner';
import SimpleDialog from '../../../../features/common/SimpleDialog';
import PageHeader from '../../../../materio/@core/components/page-header';
import ConfirmationDialog from '../../../../features/common/ConfirmationDialog';
import useApplication from '../../../hooks/useApplication.hooks';
import SectionAppBar from '../../../components/Section/SectionAppBar';
import Button from '../../../components/Button/StandardButton';
import SaveButton from '../../../components/Button/SaveButton';

type Props = {
    open: boolean;
    onClose: () => void;
    activityTypeOutcome: ActivityTypeOutcome;
};

const SubOutcomeList = ({ open, onClose, activityTypeOutcome }: Props) => {
    const [data, setData] = useState<SubOutcome[]>([]);
    const { isLoading, setIsLoading } = useApplication();
    const [recordsLoaded, setRecordsLoaded] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const { options } = useOptions('OUTCOMEACTION');
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== 'idle';

    useEffect(() => {
        if (activityTypeOutcome) {
            loadRecords();
        }
    }, [activityTypeOutcome]);

    const loadRecords = async () => {
        setIsLoading(true);
        try {
            const response = await client.get(`/api/activity-types/outcomes/sub-outcomes`, {
                params: { ActivityTypeOutcomeId: activityTypeOutcome.id },
            });
            setData(response.data);
        } finally {
            setRecordsLoaded(true);
            setIsLoading(false);
        }
    };

    const handleConfirmDelete = async (row: SubOutcome) => {
        try {
            await execute();
            const temp = cloneDeep(data);
            const rowIndex = temp.findIndex((s) => s.id === row.id);

            if (rowIndex > -1) {
                temp[rowIndex].isDeleted = true;
                setData(temp);
                setIsDirty(true);
            }

            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Sub-outcome successfully deleted';
                s.snackbarOpen = true;
            });
        } catch (err) {
            // Handle error
        } finally {
            reset();
        }
    };

    const onCloseDialog = () => {
        if (isDirty) {
            const result = confirm('You have unsaved changes. Are you sure you wish to close the page?');
            if (!result) {
                return;
            }
        }
        setData([]);
        onClose();
    };

    const onSaveDialog = async () => {
        const response = await client.post<ApiResponseType>('/api/activity-types/outcomes/sub-outcomes', {
            items: data,
        });
        if (response.data.succeeded) {
            loadRecords();
            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Activity Sub Outcomes Saved Successfully';
                s.snackbarOpen = true;
            });
        } else {
            alert('error updating outcomes');
        }
    };

    const onUpdate = (index: number, prop: string, value: any) => {
        const temp = cloneDeep(data);
        temp[index][prop] = value;
        setData(temp);
        setIsDirty(true);
    };

    const onAddRow = () => {
        const temp = cloneDeep(data);
        temp.push({
            id: uuid(),
            isActive: false,
            isMeaningfull: false,
            isDC: false,
            isMQL: false,
            orderBy: temp.length + 1,
            stages: [] as string[],
            newStageId: '00000000-0000-0000-0000-000000000000',
            activityOutcomeId: activityTypeOutcome.id,
        } as SubOutcome);
        setData(temp);
        setIsDirty(true);
    };

    if (!open) return null;

    if (!recordsLoaded) return <FallbackSpinner />;
    if (!data) return <Box>Error fetching outcome types</Box>;

    return (
        <SimpleDialog open={open} onClose={onCloseDialog} disabledCloseOnEscape={true} size="xl" fullScreen={true}>
            <SectionAppBar
                title="Edit Sub-Outcomes"
                subTitle={`Edit Sub-Outcomes for ${activityTypeOutcome.name}`}
                buttons={
                    <>
                        <Button color="secondary" onClick={onAddRow} icon={<AddIcon />}>
                            Add Sub-Outcome
                        </Button>
                        <SaveButton color="secondary" icon={<SaveAltIcon />} onClick={onSaveDialog}>
                            Save Sub-Outcomes
                        </SaveButton>
                        <Button color="secondary" icon={<CloseIcon />} onClick={onCloseDialog}>
                            Close
                        </Button>
                    </>
                }
            />

            {data
                .filter((x) => !x.isDeleted)
                .map((item, index) => (
                    <SubOutcomeRow
                        key={item.id}
                        item={item}
                        index={index}
                        onUpdate={onUpdate}
                        handleConfirmDelete={handleConfirmDelete}
                        options={options}
                    />
                ))}

            <ConfirmationDialog
                open={showModal}
                onConfirm={resolve}
                onCancel={reject}
                title="Delete?"
                message="Are you sure you want to delete the sub-outcome?"
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
            />
        </SimpleDialog>
    );
};

export default SubOutcomeList;
